import { UnitEmergencyDto } from '@mark43/cad-api';
import { HStack, Button, Flex, Icon, Banner, Text } from '@cad-arc';
import { useUnitEmergency } from './use-unit-emergency';
import { useApplicationType } from '../common/hooks/use-application-type';
import { FR_EVENT_DETAILS_TABS } from '../fr-event-details-page/constants';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { isValidLatLng } from '../location/helpers';
import { useViewLocationOnMap } from '../location/hooks/use-view-location-on-map';
import { ViewLocationOnMapArgs } from '../location/hooks/types';
import { EsriMapEntityTypeEnum } from '../esri/types';
import { useOpenEvent } from '../common/hooks/use-open-event';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';

function UnitEmergencyTitle({ emergency }: { emergency: UnitEmergencyDto }) {
    const { unitCallSign } = emergency;
    return (
        <HStack>
            <span>Unit Emergency</span>
            <Text color="inherit">{unitCallSign}</Text>
        </HStack>
    );
}

function UnitEmergencyDescription({
    emergency,
    handleOpenEvent,
}: {
    emergency: UnitEmergencyDto;
    handleOpenEvent: () => void;
}) {
    const { location, agencyEventNumber } = emergency;
    const { latitude, longitude, streetAddress } = location;
    const validLatLng = isValidLatLng(latitude, longitude);
    const viewLocationOnMap = useViewLocationOnMap();
    const navigateToMap = () => {
        if (validLatLng) {
            viewLocationOnMap({
                latitude,
                longitude,
                entityType: EsriMapEntityTypeEnum.UNIT,
                entityId: emergency?.unitId,
            } as ViewLocationOnMapArgs);
        }
    };
    return (
        <Flex gap="4" pl="4">
            {location?.areCoordinatesVerified ? (
                <Button
                    variant="solid"
                    colorVariant="dangerous"
                    leadingVisual={<Icon icon="Map" />}
                    onClick={navigateToMap}
                >
                    <Text color="inherit">{streetAddress}</Text>
                </Button>
            ) : (
                <Text color="inherit">{streetAddress}</Text>
            )}
            {agencyEventNumber && (
                <Button
                    variant="solid"
                    colorVariant="dangerous"
                    leadingVisual={<Icon icon="CADEvent" />}
                    onClick={handleOpenEvent}
                >
                    <Text color="inherit">#{agencyEventNumber}</Text>
                </Button>
            )}
        </Flex>
    );
}

export function UnitEmergencyBanner({
    emergency,
}: {
    emergency: UnitEmergencyDto;
}): React.ReactElement {
    const { acknowledgeUnitEmergency } = useUnitEmergency();
    const openEvent = useOpenEvent();
    const drawerControls = useDrawerControls();
    const { isFirstResponder } = useApplicationType();
    const modalControls = useFullscreenModalControls();

    const acknowledgeEmergency = () => {
        const ackUnitEmergencyRequest = {
            unitId: emergency.unitId,
        };
        acknowledgeUnitEmergency(ackUnitEmergencyRequest);
    };

    const handleOpenEvent = () => {
        openEvent({
            eventId: emergency.agencyEventId,
            frEventDetailsTabName: FR_EVENT_DETAILS_TABS.NARRATIVE,
            shouldFocusOnCadEventPanel: true,
        });
        if (!isFirstResponder && emergency.agencyEventId) {
            drawerControls.open({
                type: 'manage-units',
                props: {
                    eventId: emergency.agencyEventId,
                },
            });
        }
        if (isFirstResponder && emergency.agencyEventId) {
            const data = {
                eventId: emergency.agencyEventId,
                eventLocation: emergency.location.streetAddress?.toString(),
            };
            modalControls.open({
                type: 'UNIT_SELF_ASSIGNMENT_MODAL',
                props: {
                    selectedEvent: data,
                    assignedEvent: data,
                },
            });
        }
    };

    const bannerActions = [
        {
            children: 'Acknowledge',
            variant: 'outline',
            onClick: acknowledgeEmergency,
        },
    ];
    if (emergency?.agencyEventId) {
        bannerActions.push({
            children: 'Assist',
            variant: 'solid',
            onClick: handleOpenEvent,
        });
    }

    return (
        <Banner
            status="error"
            data-testid="unit-emergency-banner"
            data-banner-id={emergency.id}
            actions={bannerActions}
            title={<UnitEmergencyTitle emergency={emergency} />}
            description={
                <UnitEmergencyDescription emergency={emergency} handleOpenEvent={handleOpenEvent} />
            }
        />
    );
}

export function UnitEmergencyBanners() {
    const unitEmergency = useUnitEmergency();
    const { filteredUnAckedUnitEmergencies } = unitEmergency;

    return (
        <>
            {filteredUnAckedUnitEmergencies.map((emergency: UnitEmergencyDto) =>
                emergency ? <UnitEmergencyBanner key={emergency.id} emergency={emergency} /> : <></>
            )}
        </>
    );
}
